import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import '../ui-pages.css';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';

// Channel glyphs — stroke style, matching the Automations page platform icons.
const CIc = ({ children }) => <svg width="21" height="21" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">{children}</svg>;
const WhatsAppIcon = () => <CIc><path d="M21 11.5a8.5 8.5 0 0 1-12.6 7.4L3 21l2.2-5.3A8.5 8.5 0 1 1 21 11.5z" /><path d="M8.5 8.5c0 4 3 7 7 7" /></CIc>;
const InstaIcon = () => <CIc><rect x="3" y="3" width="18" height="18" rx="5" /><circle cx="12" cy="12" r="3.5" /><line x1="17.5" y1="6.5" x2="17.5" y2="6.5" /></CIc>;
const FbIcon = () => <CIc><path d="M12 2C6.5 2 2 6.1 2 11.2c0 2.9 1.4 5.4 3.7 7.1V22l3.4-1.9c.9.3 1.9.4 2.9.4 5.5 0 10-4.1 10-9.2S17.5 2 12 2z" /><path d="m7 13 3-3 2 2 3-3" /></CIc>;
const ShopIcon = () => <CIc><path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z" /><line x1="3" y1="6" x2="21" y2="6" /><path d="M16 10a4 4 0 0 1-8 0" /></CIc>;

// Small connection-tab switcher shown inside a card when there are 2+ connections.
const ConnTabs = ({ items, active, onPick, color }) => {
  if (!items || items.length < 2) return null;
  return (
    <div className="ic-conntabs">
      {items.map(c => (
        <button key={c.connId} className={`ic-conntab ${active === c.connId ? 'on' : ''}`}
          onClick={() => onPick(c.connId)} style={active === c.connId ? { color: '#F9F4EE', borderColor: '#111111', background: '#111111' } : {}}>
          {c.label}
        </button>
      ))}
    </div>
  );
};

const Integrations = () => {
  const { workspaceUuid } = useParams();
  const [data, setData] = useState({ limits: {}, whatsapp: [], instagram: [], facebook: [], shopify: [], website: { connected: true } });
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState({});          // platform -> selected connId
  const [copiedKey, setCopiedKey] = useState('');

  // WhatsApp connect modal
  const [showWa, setShowWa] = useState(false);
  const [waStep, setWaStep] = useState(1);
  const [waForm, setWaForm] = useState({ phone: '', displayName: '', otp: '' });
  const [waBusy, setWaBusy] = useState(false);
  const [waErr, setWaErr] = useState('');
  const [waAlready, setWaAlready] = useState(false);
  const [igBusy, setIgBusy] = useState(false);

  // WhatsApp "bring your own Meta app" connect modal
  const [showWaApp, setShowWaApp] = useState(false);
  const [waAppForm, setWaAppForm] = useState({ appId: '', appSecret: '', wabaId: '', phoneNumberId: '', accessToken: '', verifyToken: '' });
  const [waAppBusy, setWaAppBusy] = useState(false);
  const [waAppErr, setWaAppErr] = useState('');

  // PostEx courier connect
  const [pxToken, setPxToken] = useState('');
  const [pxBusy, setPxBusy] = useState(false);
  const [pxErr, setPxErr] = useState('');
  const connectPostex = async () => {
    setPxErr('');
    if (!pxToken.trim()) { setPxErr('Enter your PostEx API token.'); return; }
    setPxBusy(true);
    try {
      await axios.post(`${API_URL}/api/integrations/postex/connect/${workspaceUuid}`, { token: pxToken.trim() });
      setPxToken(''); fetchIntegrations();
    } catch (err) { setPxErr(err.response?.data?.message || 'Could not connect PostEx.'); }
    finally { setPxBusy(false); }
  };
  const disconnectPostex = async () => {
    if (!window.confirm('Disconnect PostEx from this workspace?')) return;
    try { await axios.post(`${API_URL}/api/integrations/postex/disconnect/${workspaceUuid}`); fetchIntegrations(); }
    catch (err) { alert('Disconnect failed: ' + (err.response?.data?.message || err.message)); }
  };

  const setActiveFor = (p, id) => setActive(prev => ({ ...prev, [p]: id }));

  const fetchIntegrations = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/workspaces/${workspaceUuid}/integrations`);
      setData(res.data);
    } catch (err) { console.error('Error fetching integrations:', err); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchIntegrations(); /* eslint-disable-next-line */ }, [workspaceUuid]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const labels = { shopify: 'Shopify', instagram: 'Instagram', facebook: 'Facebook Messenger' };
    const provider = Object.keys(labels).find(p => params.get(p));
    if (provider) {
      const outcome = params.get(provider);
      if (outcome === 'connected') alert(`${labels[provider]} connected${params.get('page') ? `: ${params.get('page')}` : ''}`);
      else if (outcome === 'error') alert(`${labels[provider]} failed: ${params.get('reason') || 'unknown'}${params.get('detail') ? `\n${params.get('detail')}` : ''}`);
      window.history.replaceState({}, '', window.location.pathname);
    }
    fetchIntegrations();
    // eslint-disable-next-line
  }, []);

  // ---- connect actions ----
  const goInstall = (platform, query = '') => { window.location.href = `${API_URL}/api/integrations/${platform}/install/${workspaceUuid}${query}`; };

  const connectInstagram = () => goInstall('instagram');
  const connectFacebook = () => goInstall('facebook');

  const connectIgViaFb = async () => {
    setIgBusy(true);
    try {
      const res = await axios.post(`${API_URL}/api/integrations/instagram/connect-via-facebook/${workspaceUuid}`);
      alert(`Instagram connected via Facebook${res.data?.username ? ': @' + res.data.username : ''}`);
      fetchIntegrations();
    } catch (err) { alert('Could not connect Instagram via Facebook: ' + (err.response?.data?.message || err.message)); }
    finally { setIgBusy(false); }
  };

  const disconnect = async (platform, connectionId) => {
    if (!window.confirm('Disconnect this account from the workspace?')) return;
    try {
      const path = platform === 'whatsapp' ? `whatsapp/host/disconnect/${workspaceUuid}`
        : platform === 'shopify' ? 'shopify/disconnect'
          : `${platform}/disconnect/${workspaceUuid}`;
      const body = platform === 'shopify' ? { connectionId } : { connectionId };
      await axios.post(`${API_URL}/api/integrations/${path}`, body);
      fetchIntegrations();
    } catch (err) { alert('Disconnect failed: ' + (err.response?.data?.message || err.message)); }
  };

  // WhatsApp hosted connect (auto-fills the next free slot on the backend)
  const openWa = () => { setWaForm({ phone: '', displayName: '', otp: '' }); setWaStep(1); setWaErr(''); setWaAlready(false); setShowWa(true); };
  const waSend = async () => {
    setWaErr('');
    if (!waForm.phone.trim() || !waForm.displayName.trim()) { setWaErr('Enter the phone number and a display name.'); return; }
    setWaBusy(true);
    try {
      const res = await axios.post(`${API_URL}/api/integrations/whatsapp/host/add/${workspaceUuid}`, { phone: waForm.phone.trim(), displayName: waForm.displayName.trim() });
      setWaAlready(!!res.data.alreadyVerified); setWaStep(2);
    } catch (err) { setWaErr(err.response?.data?.message || 'Could not start connection.'); }
    finally { setWaBusy(false); }
  };
  const waVerify = async () => {
    setWaErr('');
    if (!waAlready && !waForm.otp.trim()) { setWaErr('Enter the code you received on WhatsApp.'); return; }
    setWaBusy(true);
    try {
      await axios.post(`${API_URL}/api/integrations/whatsapp/host/verify/${workspaceUuid}`, { code: waForm.otp.trim(), alreadyVerified: waAlready });
      setShowWa(false); fetchIntegrations();
    } catch (err) { setWaErr(err.response?.data?.message || 'Verification failed.'); }
    finally { setWaBusy(false); }
  };

  // WhatsApp "bring your own Meta app" connect — the tenant wires their own app +
  // number to our webhook and supplies their permanent token + ids.
  const waWebhookUrl = `${window.location.origin}/api/integrations/whatsapp/webhook`;
  const openWaApp = () => { setWaAppForm({ appId: '', appSecret: '', wabaId: '', phoneNumberId: '', accessToken: '', verifyToken: '' }); setWaAppErr(''); setShowWaApp(true); };
  const waAppConnect = async () => {
    setWaAppErr('');
    const f = waAppForm;
    if (!f.wabaId.trim() || !f.phoneNumberId.trim() || !f.accessToken.trim() || !f.verifyToken.trim()) {
      setWaAppErr('WABA ID, Phone Number ID, Access Token and Verify Token are required.'); return;
    }
    setWaAppBusy(true);
    try {
      await axios.post(`${API_URL}/api/integrations/whatsapp/connect/${workspaceUuid}`, {
        appId: f.appId.trim(), appSecret: f.appSecret.trim(),
        wabaId: f.wabaId.trim(), phoneNumberId: f.phoneNumberId.trim(),
        accessToken: f.accessToken.trim(), verifyToken: f.verifyToken.trim()
      });
      setShowWaApp(false); fetchIntegrations();
    } catch (err) { setWaAppErr(err.response?.data?.message || 'Connection failed.'); }
    finally { setWaAppBusy(false); }
  };

  if (loading) {
    const Bar = ({ w, h = 10, r = 999, s }) => <div className="cl-skel" style={{ width: w, height: h, borderRadius: r, ...s }} />;
    const CardSkel = () => (
      <div className="ic-card">
        <div className="ic-head">
          <div className="cl-skel" style={{ width: 42, height: 42, borderRadius: '50%', flex: 'none' }} />
          <div className="ic-title-wrap" style={{ display: 'flex', flexDirection: 'column', gap: 8, paddingTop: 4 }}>
            <Bar w="60%" h={13} /><Bar w="90%" h={9} />
          </div>
          <Bar w={92} h={22} r={999} s={{ flex: 'none' }} />
        </div>
        <div className="ic-body" style={{ display: 'flex', gap: 8 }}><Bar w={140} h={32} r={999} /><Bar w={120} h={32} r={999} /></div>
      </div>
    );
    const Section = ({ n }) => (
      <div className="ic-section">
        <div className="ic-section-head"><Bar w={170} h={13} /></div>
        <div className="ic-grid cl-skel-stagger">{Array.from({ length: n }).map((_, i) => <CardSkel key={i} />)}</div>
      </div>
    );
    return (
      <div className="ui-page">
        <style>{intCss}</style>
        <div className="ic-topbar">
          <div><h1 className="ui-title">Integrations</h1><p className="ui-sub">Connect your channels — your workspace can hold multiple accounts per channel.</p></div>
        </div>
        <Section n={4} />
        <Section n={2} />
      </div>
    );
  }

  const limit = (p) => data.limits?.[p] ?? 1;
  const list = (p) => data[p] || [];
  const activeConn = (p) => {
    const items = list(p);
    if (!items.length) return null;
    return items.find(c => c.connId === active[p]) || items[0];
  };
  const canAdd = (p) => list(p).length < limit(p);

  // Generic messaging card (whatsapp/instagram/facebook)
  const MsgCard = ({ platform, Icon, bg, color, title, desc, connectButtons }) => {
    const conns = list(platform);
    const sel = activeConn(platform);
    const n = conns.length;
    return (
      <div className="ic-card">
        <div className="ic-head">
          <div className="ic-icon" style={{ background: bg, color }}><Icon /></div>
          <div className="ic-title-wrap">
            <h3 className="ic-title">{title}</h3>
            <p className="ic-desc">{desc}</p>
          </div>
          <span className={`ic-badge ${n ? 'on' : ''}`}>
            <span className="ic-dot" />{n ? `${n} connected${limit(platform) > 1 ? ` / ${limit(platform)}` : ''}` : 'Not connected'}
          </span>
        </div>
        <div className="ic-body">
          {n > 0 && (
            <>
              <ConnTabs items={conns} active={sel?.connId} onPick={(id) => setActiveFor(platform, id)} color={color} />
              <div className="ic-connrow">
                <div className="ic-conn-l">
                  <span className="ic-status-dot" />
                  <div className="ic-conn-info">
                    <span className="ic-connlabel">{sel?.label}</span>
                    <span className="ic-connsub">{sel?.connectedVia === 'demo' || /demo data/i.test(sel?.label || '') ? 'Demo data — simulated connection' : 'Connected'}</span>
                  </div>
                </div>
                <button className="ic-btn ic-btn-ghostdanger" onClick={() => disconnect(platform, sel?.connId)}>Disconnect</button>
              </div>
            </>
          )}
          {canAdd(platform) && <div className="ic-actions">{connectButtons}</div>}
        </div>
      </div>
    );
  };

  return (
    <div className="ui-page">
      <style>{intCss}</style>
      <div className="ic-topbar">
        <div>
          <h1 className="ui-title">Integrations</h1>
          <p className="ui-sub">Connect your channels — your workspace can hold multiple accounts per channel.</p>
        </div>
        <div className="ic-overview">
          <div className="ic-ov-num">{list('whatsapp').length + list('instagram').length + list('facebook').length + list('shopify').filter(s => s.connected).length + (data.postex?.connected ? 1 : 0) + 1}</div>
          <div className="ic-ov-label">Active connections</div>
        </div>
      </div>

      <div className="ic-section">
        <div className="ic-section-head">
          <h2>Messaging Channels</h2>
          <span className="ic-section-count">{list('whatsapp').length + list('instagram').length + list('facebook').length + 1} connected</span>
        </div>
        <div className="ic-grid">
          <MsgCard platform="whatsapp" Icon={WhatsAppIcon} bg="#dcfce7" color="#16a34a" title="WhatsApp Business" desc="Official WhatsApp integration via Meta Business Platform."
            connectButtons={<>
              <button className="ic-btn ic-btn-primary" onClick={openWa}>+ Connect WhatsApp</button>
              <button className="ic-btn ic-btn-outline" onClick={openWaApp} title="Connect a number that lives on your own Meta app">Own Meta App</button>
            </>} />

          <MsgCard platform="instagram" Icon={InstaIcon} bg="#fce7f3" color="#db2777" title="Instagram Messaging" desc="Direct messages from Instagram."
            connectButtons={<>
              <button className="ic-btn ic-btn-primary" onClick={connectInstagram}>+ Connect Instagram</button>
              <button className={`ic-btn ic-btn-outline ${!list('facebook').length || igBusy ? 'disabled' : ''}`} disabled={!list('facebook').length || igBusy}
                onClick={connectIgViaFb} title={!list('facebook').length ? 'Connect Facebook Messenger first' : 'Use the IG account linked to your Page'}>
                {igBusy ? 'Connecting…' : '+ via Facebook'}
              </button>
            </>} />

          <MsgCard platform="facebook" Icon={FbIcon} bg="#e0e7ff" color="#1d4ed8" title="Facebook Messenger" desc="Connect Facebook Pages to handle Messenger chats."
            connectButtons={<button className="ic-btn ic-btn-primary" onClick={connectFacebook}>+ Connect Messenger</button>} />

          {/* Website Chat Widget */}
        {(() => {
          const fullPageUrl = `${window.location.origin}/chat/${workspaceUuid}`;
          const embedUrl = `${window.location.origin}/chat/${workspaceUuid}/embed`;
          const iframeSnippet = `<iframe src="${embedUrl}" style="width:100%;max-width:480px;height:720px;border:0;border-radius:16px" title="Chat"></iframe>`;
          const copyVal = (k, t) => { navigator.clipboard.writeText(t); setCopiedKey(k); setTimeout(() => setCopiedKey(''), 1300); };
          const copyBtn = (k, label, value) => {
            const done = copiedKey === k;
            return <button key={k} onClick={() => copyVal(k, value)} title={`Copy ${label}`} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 12px', background: done ? '#E4F1E9' : '#FFFFFF', color: done ? '#0F6E56' : '#3A3A37', border: `1px solid ${done ? '#0F6E56' : '#E7E1D6'}`, borderRadius: 999, fontWeight: 700, fontSize: 12, cursor: 'pointer', whiteSpace: 'nowrap' }}>{done ? '✓ Copied' : label}</button>;
          };
          return (
            <div className="ic-card">
              <div className="ic-head">
                <div className="ic-icon" style={{ background: '#e0e7ff', color: '#4338ca' }}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" /></svg>
                </div>
                <div className="ic-title-wrap"><h3 className="ic-title">Website Chat Widget</h3><p className="ic-desc">Embed on any website or share as a full-page chat.</p></div>
                <span className="ic-badge on"><span className="ic-dot" />Always on</span>
              </div>
              <div className="ic-body">
                <div className="ic-actions">
                  {copyBtn('full', 'Full-Page URL', fullPageUrl)}
                  {copyBtn('embed', 'Embed URL', embedUrl)}
                  {copyBtn('iframe', 'Iframe snippet', iframeSnippet)}
                </div>
              </div>
            </div>
          );
        })()}
        </div>
      </div>

      <div className="ic-section">
        <div className="ic-section-head">
          <h2>Commerce &amp; Delivery</h2>
          <span className="ic-section-count">{list('shopify').filter(s => s.connected).length + (data.postex?.connected ? 1 : 0)} connected</span>
        </div>
        <div className="ic-grid">
          {/* Shopify — one row per configured store */}
          <div className="ic-card">
            <div className="ic-head">
              <div className="ic-icon" style={{ background: '#FAF7F1', color: '#95BF47' }}><ShopIcon /></div>
              <div className="ic-title-wrap"><h3 className="ic-title">Shopify</h3><p className="ic-desc">Sync orders &amp; abandoned checkouts. Stores are configured by your platform admin.</p></div>
              <span className="ic-badge on"><span className="ic-dot" />{list('shopify').filter(s => s.connected).length} / {limit('shopify')} connected</span>
            </div>
            <div className="ic-body">
              {list('shopify').length === 0 ? (
                <div className="ic-empty">No Shopify stores configured yet. Ask your platform admin to add store credentials.</div>
              ) : (
                <div className="ic-stores">
                  {list('shopify').map(s => (
                    <div key={s.connId || s.shopDomain} className="ic-store">
                      <div className="ic-store-l">
                        <span className="ic-store-dom">{s.shopDomain || '—'}</span>
                        <span className={`ic-badge sm ${s.connected ? 'on' : ''}`}><span className="ic-dot" />{s.connected ? 'Connected' : s.configured ? 'Ready' : 'Awaiting setup'}</span>
                      </div>
                      <div style={{ display: 'flex', gap: 8 }}>
                        {s.connected ? (
                          <>
                            {s.shopDomain && <a className="ic-btn ic-btn-outline" style={{ textDecoration: 'none' }} target="_blank" rel="noreferrer" href={`https://${s.shopDomain}/admin/themes/current/editor?context=apps`}>Enable widget</a>}
                            <button className="ic-btn ic-btn-outline" style={{ color: '#B23A3A', borderColor: '#B23A3A' }} onClick={() => disconnect('shopify', s.connId || s.shopDomain)}>Disconnect</button>
                          </>
                        ) : (
                          <button className="ic-btn ic-btn-outline" disabled={!s.configured} onClick={() => goInstall('shopify', `?shop=${encodeURIComponent(s.shopDomain)}`)}>Connect</button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* PostEx courier */}
          <div className="ic-card">
            <div className="ic-head">
              <div className="ic-icon" style={{ background: '#FAF7F1', color: '#111111' }}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="1" y="3" width="15" height="13" /><path d="M16 8h4l3 3v5h-7V8z" /><circle cx="5.5" cy="18.5" r="2.5" /><circle cx="18.5" cy="18.5" r="2.5" /></svg>
              </div>
              <div className="ic-title-wrap"><h3 className="ic-title">PostEx</h3><p className="ic-desc">Courier delivery tracking for your Shopify orders (Pakistan).</p></div>
              <span className={`ic-badge ${data.postex?.connected ? 'on' : ''}`}><span className="ic-dot" />{data.postex?.connected ? 'Connected' : 'Not connected'}</span>
            </div>
            <div className="ic-body">
              {data.postex?.connected ? (
                <div className="ic-connrow">
                  <span className="ic-connlabel" style={{ color: '#111111' }}>PostEx API connected</span>
                  <button className="ic-btn ic-btn-outline" style={{ color: '#B23A3A', borderColor: '#B23A3A' }} onClick={disconnectPostex}>Disconnect</button>
                </div>
              ) : (
                <div className="ic-actions" style={{ alignItems: 'center' }}>
                  <input value={pxToken} onChange={e => setPxToken(e.target.value)} placeholder="Paste your PostEx API token" style={{ ...inp, flex: 1, minWidth: 200, fontFamily: "'IBM Plex Mono', monospace", fontSize: 13 }} />
                  <button className="ic-btn ic-btn-primary" disabled={pxBusy} onClick={connectPostex}>{pxBusy ? 'Connecting…' : 'Connect'}</button>
                </div>
              )}
              {pxErr && <div className="ic-err">{pxErr}</div>}
            </div>
          </div>
        </div>
      </div>

      {/* WhatsApp connect modal */}
      {showWa && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(17,17,17,0.30)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
          onClick={(e) => { if (e.target === e.currentTarget && !waBusy) setShowWa(false); }}>
          <div style={{ background: '#fff', width: '100%', maxWidth: 440, borderRadius: 20, border: '1px solid #E7E1D6', padding: '26px 28px', boxShadow: '4px 4px 0 rgba(17,17,17,.2)' }}>
            <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, letterSpacing: '-0.03em' }}>Connect WhatsApp</h2>
            <p style={{ margin: '4px 0 18px', fontSize: 12.5, color: '#6B6862' }}>{waStep === 1 ? 'Enter the number and a display name.' : 'Enter the code sent to the number.'}</p>
            {waStep === 1 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <input value={waForm.phone} onChange={e => setWaForm({ ...waForm, phone: e.target.value })} placeholder="+92 300 1234567" style={inp} />
                <input value={waForm.displayName} onChange={e => setWaForm({ ...waForm, displayName: e.target.value })} placeholder="Display name" style={inp} />
              </div>
            ) : (
              <div>
                {waAlready ? <div style={{ fontSize: 13, color: '#3A3A37', background: '#FAF7F1', border: '1px solid #E7E1D6', borderRadius: 14, padding: '11px 13px' }}>Already verified — press Connect to finish.</div>
                  : <input value={waForm.otp} onChange={e => setWaForm({ ...waForm, otp: e.target.value })} placeholder="6-digit code" inputMode="numeric" style={{ ...inp, letterSpacing: '0.15em', fontSize: 16, fontFamily: "'IBM Plex Mono', monospace" }} />}
                <button type="button" onClick={() => { setWaStep(1); setWaErr(''); }} style={{ marginTop: 8, background: 'none', border: 'none', color: '#6B6862', fontSize: 12, fontWeight: 700, cursor: 'pointer', padding: 0 }}>← Change number</button>
              </div>
            )}
            {waErr && <div style={{ fontSize: 12.5, color: '#B23A3A', background: '#F6E4E1', border: '1px solid #E7E1D6', padding: '9px 12px', borderRadius: 14, fontWeight: 600, marginTop: 12 }}>{waErr}</div>}
            <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
              <button className="ui-btn ui-btn-ghost" style={{ flex: 1, justifyContent: 'center' }} disabled={waBusy} onClick={() => setShowWa(false)}>Cancel</button>
              {waStep === 1
                ? <button className="ui-btn" style={{ flex: 2, justifyContent: 'center' }} disabled={waBusy} onClick={waSend}>{waBusy ? 'Sending…' : 'Send code'}</button>
                : <button className="ui-btn" style={{ flex: 2, justifyContent: 'center' }} disabled={waBusy} onClick={waVerify}>{waBusy ? 'Connecting…' : 'Connect'}</button>}
            </div>
          </div>
        </div>
      )}

      {/* WhatsApp — bring your own Meta app modal */}
      {showWaApp && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(17,17,17,0.30)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
          onClick={(e) => { if (e.target === e.currentTarget && !waAppBusy) setShowWaApp(false); }}>
          <div style={{ background: '#fff', width: '100%', maxWidth: 520, maxHeight: '92vh', overflowY: 'auto', borderRadius: 20, border: '1px solid #E7E1D6', padding: '26px 28px', boxShadow: '4px 4px 0 rgba(17,17,17,.2)' }}>
            <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, letterSpacing: '-0.03em' }}>Connect via your own Meta App</h2>
            <p style={{ margin: '4px 0 16px', fontSize: 12.5, color: '#6B6862' }}>
              Use this if your WhatsApp number lives on <strong>your own</strong> Meta app. Paste the credentials from
              your app's <em>WhatsApp → API Setup</em> screen.
            </p>

            {/* Step 1 — wire the webhook on the tenant's app */}
            <div style={{ background: '#E4F1E9', border: '1px solid #E7E1D6', borderRadius: 14, padding: '13px 15px', marginBottom: 16 }}>
              <div style={{ fontSize: 12, fontWeight: 800, color: '#0F6E56', textTransform: 'uppercase', letterSpacing: '0.03em', marginBottom: 8 }}>1 · Configure your app's webhook</div>
              <div style={{ fontSize: 12.5, color: '#0F6E56', marginBottom: 6 }}>In your Meta app → WhatsApp → Configuration, set the Callback URL to:</div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 10 }}>
                <code style={{ flex: 1, fontSize: 12, background: '#fff', border: '1px solid #E7E1D6', borderRadius: 8, padding: '8px 10px', wordBreak: 'break-all', color: '#111111', fontFamily: "'IBM Plex Mono', monospace" }}>{waWebhookUrl}</code>
                <button type="button" onClick={() => { navigator.clipboard.writeText(waWebhookUrl); setCopiedKey('wahook'); setTimeout(() => setCopiedKey(''), 1300); }}
                  style={{ padding: '8px 12px', background: copiedKey === 'wahook' ? '#E4F1E9' : '#fff', color: '#0F6E56', border: '1px solid #E7E1D6', borderRadius: 999, fontWeight: 700, fontSize: 12, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                  {copiedKey === 'wahook' ? '✓ Copied' : 'Copy'}</button>
              </div>
              <div style={{ fontSize: 12.5, color: '#0F6E56' }}>Use the <strong>same Verify Token</strong> you enter below, then subscribe the <code style={{ background: '#fff', padding: '1px 5px', borderRadius: 5, border: '1px solid #E7E1D6', fontFamily: "'IBM Plex Mono', monospace" }}>messages</code> field.</div>
            </div>

            {/* Step 2 — credentials */}
            <div style={{ fontSize: 12, fontWeight: 800, color: '#3A3A37', textTransform: 'uppercase', letterSpacing: '0.03em', marginBottom: 10 }}>2 · Your app credentials</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 11 }}>
              <Field label="WhatsApp Business Account ID (WABA ID) *" value={waAppForm.wabaId} onChange={v => setWaAppForm({ ...waAppForm, wabaId: v })} placeholder="e.g. 1489415659553666" />
              <Field label="Phone Number ID *" value={waAppForm.phoneNumberId} onChange={v => setWaAppForm({ ...waAppForm, phoneNumberId: v })} placeholder="e.g. 705123456789012" />
              <Field label="Permanent Access Token *" value={waAppForm.accessToken} onChange={v => setWaAppForm({ ...waAppForm, accessToken: v })} placeholder="System-user token (never expires)" mono />
              <Field label="Verify Token *" value={waAppForm.verifyToken} onChange={v => setWaAppForm({ ...waAppForm, verifyToken: v })} placeholder="Any secret string — must match your webhook" />
              <Field label="Meta App ID" value={waAppForm.appId} onChange={v => setWaAppForm({ ...waAppForm, appId: v })} placeholder="Optional" />
              <Field label="Meta App Secret" value={waAppForm.appSecret} onChange={v => setWaAppForm({ ...waAppForm, appSecret: v })} placeholder="Optional — enables app-secret proof" mono />
            </div>

            {waAppErr && <div style={{ fontSize: 12.5, color: '#B23A3A', background: '#F6E4E1', border: '1px solid #E7E1D6', padding: '9px 12px', borderRadius: 14, fontWeight: 600, marginTop: 14 }}>{waAppErr}</div>}
            <div style={{ display: 'flex', gap: 10, marginTop: 18 }}>
              <button className="ui-btn ui-btn-ghost" style={{ flex: 1, justifyContent: 'center' }} disabled={waAppBusy} onClick={() => setShowWaApp(false)}>Cancel</button>
              <button className="ui-btn" style={{ flex: 2, justifyContent: 'center' }} disabled={waAppBusy} onClick={waAppConnect}>{waAppBusy ? 'Connecting…' : 'Connect number'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Labeled input used in the "own Meta app" modal.
const Field = ({ label, value, onChange, placeholder, mono }) => (
  <label style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
    <span style={{ fontSize: 12, fontWeight: 700, color: '#3A3A37' }}>{label}</span>
    <input value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder}
      style={{ ...inp, ...(mono ? { fontFamily: "'IBM Plex Mono', monospace", fontSize: 13 } : {}) }} />
  </label>
);

const inp = { width: '100%', padding: '11px 13px', borderRadius: 14, border: '1px solid #E7E1D6', fontSize: 14, outline: 'none', boxSizing: 'border-box', color: '#111111' };

const intCss = `
.ic-topbar{ display:flex; align-items:flex-start; justify-content:space-between; gap:16px; margin-bottom:26px; flex-wrap:wrap; }
.ic-overview{ background:#111111; color:#F9F4EE; border-radius:14px; padding:12px 20px; text-align:center; min-width:120px; }
.ic-ov-num{ font-size:26px; font-weight:700; letter-spacing:-0.03em; line-height:1.1; }
.ic-ov-label{ font-size:11px; font-weight:700; letter-spacing:.04em; text-transform:uppercase; color:rgba(249,244,238,.55); margin-top:2px; }

.ic-section{ margin-bottom:28px; }
.ic-section-head{ display:flex; align-items:center; justify-content:space-between; gap:12px; margin-bottom:16px; }
.ic-section-head h2{ margin:0; font-size:13px; font-weight:700; color:#111111; letter-spacing:.02em; text-transform:uppercase; }
.ic-section-count{ font-size:11px; font-weight:600; color:#9C978C; background:#fff; border:1px solid #E7E1D6; padding:4px 11px; border-radius:999px; font-family:'IBM Plex Mono',monospace; white-space:nowrap; }

/* Responsive auto-fill grid — cards keep a comfortable, uniform size and reflow
   to 2–3 per row instead of two oversized panels. */
.ic-grid{ display:grid; grid-template-columns:repeat(auto-fill, minmax(340px, 1fr)); gap:16px; align-items:stretch; grid-auto-rows:1fr; }
@media (max-width: 720px){ .ic-grid{ grid-template-columns:1fr; } }

.ic-card{ background:#fff; border:1px solid #E7E1D6; border-radius:16px; padding:20px; display:flex; flex-direction:column; min-height:184px; transition:box-shadow .18s ease, transform .18s cubic-bezier(.22,1,.36,1), border-color .18s; animation:uiIn .45s ease both; }
.ic-card:hover{ box-shadow:4px 6px 0 rgba(17,17,17,.16); transform:translateY(-3px); border-color:#111111; }
.ic-head{ display:flex; align-items:flex-start; gap:13px; }
.ic-icon{ width:44px; height:44px; border-radius:50%; display:grid; place-items:center; flex:none; box-shadow:inset 0 0 0 1px rgba(17,17,17,.05); }
.ic-icon svg{ width:21px; height:21px; }
.ic-title-wrap{ min-width:0; flex:1; padding-top:1px; }
.ic-title{ margin:0; font-size:15px; font-weight:700; letter-spacing:-0.02em; color:#111111; }
.ic-desc{ margin:3px 0 0; font-size:12px; color:#6B6862; line-height:1.5; }
.ic-badge{ display:inline-flex; align-items:center; gap:6px; flex:none; padding:5px 10px; border-radius:999px; font-size:10.5px; font-weight:600; letter-spacing:.02em; text-transform:uppercase; background:#FAF7F1; color:#9C978C; white-space:nowrap; }
.ic-badge .ic-dot{ width:6px; height:6px; border-radius:50%; background:#9C978C; }
.ic-badge.on{ background:#E4F1E9; color:#0F6E56; }
.ic-badge.on .ic-dot{ background:#0F6E56; }
.ic-badge.sm{ padding:3px 9px; font-size:10px; }
.ic-body{ margin-top:auto; padding-top:16px; }
.ic-body:not(:empty){ border-top:1px solid #F1ECE2; margin-top:16px; }
.ic-card > .ic-body:first-of-type{ }

.ic-conntabs{ display:flex; gap:7px; flex-wrap:wrap; margin-bottom:10px; }
.ic-conntab{ background:#FAF7F1; border:1px solid #E7E1D6; color:#6B6862; padding:6px 13px; border-radius:999px; font-size:12px; font-weight:600; cursor:pointer; transition:all .15s; }
.ic-conntab:hover{ border-color:#111111; }
.ic-conntab.on{ background:#111111; color:#F9F4EE; border-color:#111111; font-weight:600; }
.ic-connrow{ display:flex; align-items:center; justify-content:space-between; gap:12px; background:#FAF7F1; border:1px solid #E7E1D6; border-radius:12px; padding:11px 13px; margin-bottom:10px; }
.ic-conn-l{ display:flex; align-items:center; gap:11px; min-width:0; }
.ic-status-dot{ width:8px; height:8px; border-radius:50%; background:#0F6E56; flex:none; box-shadow:0 0 0 3px #E4F1E9; }
.ic-conn-info{ display:flex; flex-direction:column; min-width:0; gap:2px; }
.ic-connlabel{ font-size:13px; font-weight:700; color:#111111; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
.ic-connsub{ font-size:9.5px; font-weight:600; color:#0F6E56; text-transform:uppercase; letter-spacing:.05em; }
.ic-actions{ display:flex; gap:8px; flex-wrap:wrap; }

.ic-btn{ display:inline-flex; align-items:center; justify-content:center; gap:6px; padding:9px 15px; border-radius:999px; font-size:12.5px; font-weight:700; cursor:pointer; border:1px solid transparent; white-space:nowrap; transition:all .12s; }
.ic-btn-outline{ background:#fff; color:#3A3A37; border-color:#E7E1D6; }
.ic-btn-outline:hover{ background:#FAF7F1; border-color:#111111; color:#111111; }
.ic-btn-primary{ background:#111111; color:#F9F4EE; box-shadow:4px 4px 0 rgba(17,17,17,.2); }
.ic-btn-primary:hover{ background:#000; }
.ic-btn-primary:active{ transform:translate(2px,2px); box-shadow:2px 2px 0 rgba(17,17,17,.2); }
.ic-btn-ghostdanger{ background:#fff; color:#B23A3A; border-color:#F1D9D5; padding:7px 13px; }
.ic-btn-ghostdanger:hover{ background:#F6E4E1; border-color:#B23A3A; }
.ic-btn.disabled, .ic-btn:disabled{ opacity:.5; cursor:not-allowed; }

.ic-stores{ display:flex; flex-direction:column; gap:8px; }
.ic-store{ display:flex; align-items:center; justify-content:space-between; gap:12px; background:#FAF7F1; border:1px solid #E7E1D6; border-radius:12px; padding:11px 13px; flex-wrap:wrap; }
.ic-store-l{ display:flex; align-items:center; gap:10px; }
.ic-store-dom{ font-size:13px; font-weight:700; color:#111111; font-family:'IBM Plex Mono',monospace; }
.ic-empty{ font-size:12.5px; color:#9C978C; background:#FAF7F1; border:1px dashed #E7E1D6; border-radius:12px; padding:16px; text-align:center; }
.ic-err{ font-size:12.5px; color:#B23A3A; background:#F6E4E1; border:1px solid #E7E1D6; padding:9px 12px; border-radius:12px; font-weight:600; margin-top:10px; }
`;

export default Integrations;
